import React, { useEffect, useRef, useState } from "react";
import { track as track_ } from "@/lib/funnel";
import Seo from "@/components/Seo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Gift, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LangToggle } from "@/components/LangToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandHome } from "@/components/BrandHome";

export const START_PATH = "/app/studio?individual=1";  // كل حساب جديد يبدأ من «خدمات حسب الطلب» ومعه هدية الترحيب

/** الإقرار (18+ والشروط) — شرط واحد لكل طرق التسجيل */
export const ConsentBox = ({ agree, setAgree, prefix = "register" }) => (
  <label className="flex items-start gap-3 rounded-xl border border-border bg-secondary/50 p-3 text-xs leading-relaxed cursor-pointer" data-testid={`${prefix}-age-terms`}>
    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-primary shrink-0" data-testid={`${prefix}-age-terms-checkbox`} />
    <span>
      أقرّ بأنني <b className="text-foreground">أبلغ 18 عامًا فأكثر</b> وأوافق على{" "}
      <Link to="/terms" target="_blank" className="text-primary underline underline-offset-2" data-testid={`${prefix}-terms-link`}>الشروط والأحكام</Link>{" "}و{" "}
      <Link to="/liability" target="_blank" className="text-primary underline underline-offset-2" data-testid={`${prefix}-liability-link`}>إقرار حدود المسؤولية</Link>{" "}و{" "}
      <Link to="/privacy" target="_blank" className="text-primary underline underline-offset-2" data-testid={`${prefix}-privacy-link`}>سياسة الخصوصية</Link>،
      وأتعهد بعدم توليد أي محتوى مخلّ أو يهدف إلى تشويه صورة أشخاص.
    </span>
  </label>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// متصفحات التطبيقات الداخلية (إنستغرام/تيك توك/سناب/فيسبوك): Google يرفض تسجيل الدخول فيها (disallowed_useragent) — نوجّه لفتح الصفحة في المتصفح أو التسجيل بالبريد
export const inAppBrowser = () => /Instagram|FBAN|FBAV|Snapchat|BytedanceWebview|musical_ly|TikTok|Twitter|LinkedInApp|; wv\)/i.test(navigator.userAgent);

export const goGoogle = (consent) => {
  if (consent) localStorage.setItem("rawaj_consent", "1");
  track_("register_submit", "", { provider: "google" });
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + START_PATH)}`;
};

export const InAppWarning = () => inAppBrowser() ? (
  <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed" data-testid="inapp-browser-warning">
    <b>أنت داخل متصفح التطبيق.</b> تسجيل Google قد لا يعمل هنا — افتح الصفحة في Safari أو Chrome (القائمة ⋯ ← فتح في المتصفح)، أو سجّل بالبريد وكلمة المرور بالأسفل.
    <button type="button" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("نُسخ الرابط — الصقه في المتصفح"); }} className="underline mr-1" data-testid="inapp-copy-link">نسخ الرابط</button>
  </div>
) : null;

export const GoogleButton = ({ disabled, label = "المتابعة عبر Google", testId = "google-register-btn", onClick }) => (
  <Button type="button" disabled={disabled} onClick={onClick}
    className="w-full gap-3 h-12 text-base font-bold bg-white text-black hover:bg-white/90 border border-border shadow-sm disabled:opacity-60" data-testid={testId}>
    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" /> {label}
  </Button>
);

export default function Register() {
  const { user, register, formatError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const track = params.get("track");
  const next = (params.get("next") || "").startsWith("/app") ? params.get("next") : "";
  useEffect(() => { if (!user) track_("register_open"); const err = sessionStorage.getItem("rawaj_auth_error"); if (err) { sessionStorage.removeItem("rawaj_auth_error"); toast.error(err); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [manual, setManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitted = useRef(false);
  useEffect(() => {
    if (user && !submitted.current) navigate(next || "/app", { replace: true });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const formStarted = useRef(false);
  const onFormStart = () => { if (!formStarted.current) { formStarted.current = true; track_("register_form_start", "", { provider: "email" }); } };
  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { track_("register_error", "", { err: "consent", provider: "email" }); return toast.error("يُشترط أن يكون عمرك 18 عامًا فأكثر والموافقة على الشروط والأحكام لإنشاء الحساب"); }
    submitted.current = true;
    setLoading(true);
    track_("register_submit", "", { provider: "email" });
    try {
      await register("", email, password, "", { over_18: true, accepted_terms: true });
      toast.success("تم إنشاء حسابك وتفعيل هدية الترحيب 🎁 صورتان + فيديو + 25 ريال رصيد");
      if (track) navigate("/app/projects", { state: { create: track } });
      else navigate(next || START_PATH, { replace: true });
    } catch (err) {
      const st = err.response?.status;
      const d = String(err.response?.data?.detail || "");
      track_("register_error", "", { err: !st ? "network" : /مسجل مسبقاً/.test(d) ? "duplicate" : st === 422 || st === 400 ? "validation" : "server", provider: "email" });
      toast.error(formatError(err.response?.data?.detail) || "فشل إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4 py-10">
      <Seo title="إنشاء حساب مجاني — هدية ترحيب فورية" description="أنشئ حسابك في رواج الذكية عبر Google أو البريد خلال ثوانٍ واحصل على صورتين وفيديو و25 ريال رصيدًا مجانًا." path="/register" />
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3 px-1">
          <BrandHome />
          <div className="flex items-center gap-2"><LangToggle className="h-8" /><ThemeToggle /></div>
        </div>
      <div className="glass rounded-3xl p-6 sm:p-8 emerald-glow animate-float-up">
        <h1 className="text-2xl font-bold text-center mb-1 mt-1">ابدأ خلال ثوانٍ</h1>
        <p className="text-muted-foreground text-center text-sm mb-4">سجّل بحساب Google وابدأ التوليد فورًا</p>
        <div className="mb-4 rounded-xl border border-accent/40 bg-accent/10 text-accent text-xs px-3 py-2.5 flex items-start gap-2" data-testid="welcome-gift-banner">
          <Gift className="w-4 h-4 shrink-0 mt-0.5" />
          <span>هدية الترحيب لكل حساب جديد تلقائيًا: <b>صورتان + فيديو + 25 ريال رصيد</b>{localStorage.getItem("rawaj_ref") ? " — وصورتان إضافيتان لأنك مدعوّ من زميل." : "."}</span>
        </div>
        <div className="space-y-3">
          <InAppWarning />
          <ConsentBox agree={agree} setAgree={setAgree} />
          <GoogleButton disabled={!agree} onClick={() => goGoogle(true)} />
          {!agree && <p className="text-[11px] text-muted-foreground text-center" data-testid="google-consent-hint">فعّل مربع الإقرار أعلاه أولًا.</p>}
        </div>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">ليس لديك Google؟</span><div className="flex-1 h-px bg-border" />
        </div>
        <button type="button" onClick={() => setManual((m) => !m)} className="w-full flex items-center justify-center gap-2 text-sm text-primary font-semibold" data-testid="register-manual-toggle">
          <Mail className="w-4 h-4" /> التسجيل بالبريد وكلمة المرور <ChevronDown className={`w-4 h-4 transition-transform ${manual ? "rotate-180" : ""}`} />
        </button>
        {manual && (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="register-manual-form">
            <div>
              <Label className="mb-1.5 block text-xs">البريد الإلكتروني</Label>
              <Input type="email" required value={email} onChange={(e) => { onFormStart(); setEmail(e.target.value); }} placeholder="you@example.com" dir="ltr" className="bg-secondary border-border text-start" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">كلمة المرور</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => { onFormStart(); setPassword(e.target.value); }} placeholder="6 أحرف على الأقل" dir="ltr" className="bg-secondary border-border text-start" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading || !agree} className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-11" data-testid="auth-register-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "إنشاء الحساب"}
            </Button>
            {!agree && <p className="text-[11px] text-muted-foreground text-center">فعّل مربع الإقرار أعلاه لإنشاء الحساب.</p>}
          </form>
        )}
        <p className="text-center text-sm text-muted-foreground mt-6">
          لديك حساب بالفعل؟{" "}
          <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-primary font-semibold hover:underline" data-testid="go-login">سجّل الدخول</Link>
        </p>
      </div>
      </div>
    </div>
  );
}
